import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Alert,
} from '@mui/material';
import { GLYPHS } from '@/components/ui';
import { PayrollTabProps, MonthlyPayrollData, WeeklyHolidayDetail } from '../../types/employee';
import { isDailyWorkerPay, isBusinessServicePay } from '../../types/employee';
import { deductionLabel } from '../../lib/payroll/display-helper';
import { useEmployeeData, useWorkScheduleData, useEmployeePayroll } from '../../hooks';
import { useAccountBookStore } from '../../store/accountBookStore';
import { calculateBusinessScale } from '../../utils/businessScale';
// pdfService는 jspdf+html2canvas(약 590KB)를 의존하므로 동적 import로 lazy load.
// PDF 생성 버튼을 누를 때만 청크 로드 → 첫 페이지 로드 시간 단축.
import WeeklyHolidayAllowanceDialog from './WeeklyHolidayAllowanceDialog';
import { PayrollToolbar } from './PayrollToolbar';
import { PayrollSpreadsheet } from './PayrollSpreadsheet';
import LaborDayAllowanceCard from './LaborDayAllowanceCard';
import SectionHeader from '../SectionHeader';
import { monthHasLaborDay } from '../../utils/laborDayAllowance';

const PayrollTab: React.FC<PayrollTabProps> = ({
  selectedMonth: controlledMonth,
  onMonthChange,
  isBusinessScale5Plus: isBusinessScale5PlusProp,
  laborDayIs5Plus,
  laborDayToggle,
  laborDaySyncDisabled,
}) => {
  const [internalMonth, setInternalMonth] = useState<Date>(new Date());
  const selectedMonth = controlledMonth ?? internalMonth;
  const setSelectedMonth = onMonthChange ?? setInternalMonth;
  const [selectedEmployee, setSelectedEmployee] = useState<MonthlyPayrollData | null>(null);
  const [payslipDialogOpen, setPayslipDialogOpen] = useState(false);
  const [holidayAllowanceDialogOpen, setHolidayAllowanceDialogOpen] = useState(false);
  const [selectedEmployeeForHoliday, setSelectedEmployeeForHoliday] = useState<MonthlyPayrollData | null>(null);

  const { employees, loading: employeesLoading, error: employeesError } = useEmployeeData();
  const { schedules, loading: schedulesLoading, error: schedulesError } = useWorkScheduleData(selectedMonth);
  const currentAccountNumber = useAccountBookStore((s) => s.currentAccountNumber);
  const currentAccountBook = useAccountBookStore((s) => s.currentAccountBook);
  const accountNumber = currentAccountNumber ?? currentAccountBook?.accountNumber ?? 1;
  const computed5Plus = useMemo(
    () => calculateBusinessScale(employees, selectedMonth, accountNumber),
    [employees, selectedMonth, accountNumber]
  );
  const is5Plus = isBusinessScale5PlusProp ?? computed5Plus;

  const payrollOptions = useMemo(
    () => ({
      is5Plus,
      getRateForDate: (empId: string) => employees.find((e) => e.id === empId)?.hourlyRate ?? 0,
      getLaborDayIncluded: (empId: string) =>
        laborDayToggle ? laborDayToggle.isIncluded(empId) : true,
      laborDayIs5Plus,
    }),
    [employees, is5Plus, laborDayIs5Plus, laborDayToggle],
  );

  const { monthlyPayrollData: monthlyData } = useEmployeePayroll(
    employees,
    schedules,
    selectedMonth,
    payrollOptions,
  );

  const loading = employeesLoading || schedulesLoading;
  const error = employeesError || schedulesError;

  const showLaborDay = monthHasLaborDay(selectedMonth);

  const activeMonthlyData = useMemo(
    () => monthlyData.filter((data) => !data.employee.isRetired),
    [monthlyData]
  );

  const retiredMonthlyData = useMemo(
    () => monthlyData.filter((data) => data.employee.isRetired),
    [monthlyData]
  );

  const payrollSummary = useMemo(() => {
    return {
      totalEmployees: activeMonthlyData.length,
      totalGrossSalary: activeMonthlyData.reduce((sum, d) => sum + d.totalSalary, 0),
      totalNetSalary: activeMonthlyData.reduce((sum, d) => sum + d.netSalary, 0),
      totalTax: activeMonthlyData.reduce((sum, d) => sum + d.tax.total, 0),
      totalInsurance: activeMonthlyData.reduce((sum, d) => sum + d.insurance.total, 0),
    };
  }, [activeMonthlyData]);

  const handleMonthChange = (date: Date) => setSelectedMonth(date);
  const handlePayslipClick = (data: MonthlyPayrollData) => {
    setSelectedEmployee(data);
    setPayslipDialogOpen(true);
  };

  const handleDownloadPdf = async () => {
    if (!selectedEmployee) return;
    try {
      const { PayrollPdfService } = await import('../../services/pdfService');
      const payslipData = PayrollPdfService.convertToPayslipData(selectedEmployee, selectedMonth);
      await PayrollPdfService.generatePayrollPdf(payslipData);
    } catch (err) {
      console.error('PDF 생성 실패:', err);
      alert('PDF 생성에 실패했습니다. 다시 시도해주세요.');
    }
  };

  const renderWeeklyDetails = (weeklyDetails: WeeklyHolidayDetail[]) => (
    <Box sx={{ mt: 2 }}>
      <SectionHeader
        eyebrow="주간별"
        title="주간별 주휴수당 상세"
      />
      {weeklyDetails.map((week) => (
        <Accordion key={week.weekNumber}>
          <AccordionSummary expandIcon={<Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>{GLYPHS.down}</Box>}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {week.weekNumber}주차 ({week.weekStartFormatted} ~ {week.weekEndFormatted})
              </Typography>
              <Typography
                component="span"
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  bgcolor: week.isEligible ? 'success.light' : 'grey.200',
                  fontSize: '0.875rem',
                }}
              >
                {week.totalWorkHours}시간
              </Typography>
              {week.isEligible && (
                <Typography component="span" color="primary.main" sx={{ fontSize: '0.875rem' }}>
                  주휴수당 {week.holidayAllowance.toLocaleString()}
                </Typography>
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <List dense>
              {week.workDays.map((workDay, idx) => (
                <ListItem key={idx}>
                  <ListItemText
                    primary={`${workDay.date.toLocaleDateString('ko-KR', {
                      month: 'short',
                      day: 'numeric',
                      weekday: 'short',
                    })} - ${workDay.workHours}시간`}
                    secondary={
                      workDay.timeDisplay ||
                      `${workDay.startTime} ~ ${workDay.endTime} (휴게: ${workDay.breakTime})`
                    }
                  />
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          gap: 2,
        }}
      >
        <Typography variant="h6" color="text.secondary">
          급여 데이터를 불러오는 중...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          급여 데이터를 불러오는데 실패했습니다: {error}
        </Alert>
        <PayrollToolbar
          selectedMonth={selectedMonth}
          onMonthChange={handleMonthChange}
          payrollSummary={payrollSummary}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PayrollToolbar
        selectedMonth={selectedMonth}
        onMonthChange={handleMonthChange}
        payrollSummary={payrollSummary}
      />
      {showLaborDay && (
        <LaborDayAllowanceCard
          monthlyData={activeMonthlyData}
          syncDisabled={laborDaySyncDisabled}
          onToggle={(employeeId, included) =>
            laborDayToggle?.setIncluded(employeeId, included) ?? Promise.resolve()
          }
        />
      )}
      <PayrollSpreadsheet
        activeData={activeMonthlyData}
        retiredData={retiredMonthlyData}
        onPayslipClick={handlePayslipClick}
      />

      <Dialog
        open={payslipDialogOpen}
        onClose={() => setPayslipDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            급여명세서 - {selectedEmployee?.employee.name}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedEmployee && (() => {
            const emp = selectedEmployee.employee;
            const isDaily = isDailyWorkerPay(emp);
            const isBusiness = isBusinessServicePay(emp);
            const workDays = selectedEmployee.workDays ?? 0;
            const infoForLabel = {
              employee: emp,
              totalWorkHours: selectedEmployee.totalWorkHours,
              baseSalary: selectedEmployee.baseSalary,
              holidayAllowance: selectedEmployee.holidayAllowance,
              totalSalary: selectedEmployee.totalSalary,
              hasInsurance: emp.hasFourInsurance,
              totalEmployeeInsurance: selectedEmployee.insurance.total,
              withholdingTax: selectedEmployee.tax.withholding,
              localTax: selectedEmployee.tax.local,
              workDays,
            };

            if (isDaily || isBusiness) {
              return (
                <Box>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">급여 기간</Typography>
                      <Typography variant="subtitle1">
                        {selectedMonth.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long' })}
                      </Typography>
                    </Grid>
                    {workDays > 0 && (
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">근무일수</Typography>
                        <Typography variant="subtitle1">{workDays}일</Typography>
                      </Grid>
                    )}
                  </Grid>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>{isDaily ? '일당 합계' : '지급 합계'}</Typography>
                    <Typography className="num">{selectedEmployee.totalSalary.toLocaleString()}</Typography>
                  </Box>
                  {isDaily && (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography>원천세</Typography>
                        <Typography className="num">{selectedEmployee.tax.withholding.toLocaleString()}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography>지방세</Typography>
                        <Typography className="num">{selectedEmployee.tax.local.toLocaleString()}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, color: 'var(--c-red-600)' }}>
                        <Typography>고용보험(근로자)</Typography>
                        <Typography className="num">-{selectedEmployee.insurance.total.toLocaleString()}</Typography>
                      </Box>
                    </>
                  )}
                  {isBusiness && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, color: 'var(--c-red-600)' }}>
                      <Typography>원천징수(3.3%)</Typography>
                      <Typography className="num">-{selectedEmployee.insurance.total.toLocaleString()}</Typography>
                    </Box>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {deductionLabel(emp, infoForLabel, workDays)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      p: 2,
                      background: 'var(--primary)',
                      borderRadius: 1,
                      color: 'white',
                      mt: 2,
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>실수령</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }} className="num">
                      {selectedEmployee.netSalary.toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              );
            }

            return (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    급여 기간
                  </Typography>
                  <Typography variant="subtitle1">
                    {selectedMonth.toLocaleDateString('ko-KR', {
                      year: 'numeric',
                      month: 'long',
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    지급일
                  </Typography>
                  <Typography variant="subtitle1">
                    {selectedEmployee.employee.payday}일
                  </Typography>
                </Grid>
              </Grid>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    지급 내역
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>기본급</Typography>
                    <Typography className="num">{selectedEmployee.baseSalary.toLocaleString()}</Typography>
                  </Box>
                  {selectedEmployee.holidayAllowance > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>주휴수당</Typography>
                      <Typography className="num">{selectedEmployee.holidayAllowance.toLocaleString()}</Typography>
                    </Box>
                  )}
                  {(selectedEmployee.overtimePay ?? 0) > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>연장 수당</Typography>
                      <Typography className="num">{selectedEmployee.overtimePay!.toLocaleString()}</Typography>
                    </Box>
                  )}
                  {(selectedEmployee.nightPay ?? 0) > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>야간 수당</Typography>
                      <Typography className="num">{selectedEmployee.nightPay!.toLocaleString()}</Typography>
                    </Box>
                  )}
                  {(selectedEmployee.holidayPay ?? 0) > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>휴일 수당</Typography>
                      <Typography className="num">{selectedEmployee.holidayPay!.toLocaleString()}</Typography>
                    </Box>
                  )}
                  {(selectedEmployee.laborDayAllowance ?? 0) > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>근로자의 날 수당</Typography>
                      <Typography className="num">{selectedEmployee.laborDayAllowance!.toLocaleString()}</Typography>
                    </Box>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      총급여
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }} className="num">{selectedEmployee.totalSalary.toLocaleString()}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    공제 내역
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>국민연금</Typography>
                    <Typography className="num">{selectedEmployee.insurance.pension.toLocaleString()}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>건강보험</Typography>
                    <Typography className="num">{selectedEmployee.insurance.health.toLocaleString()}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>고용보험</Typography>
                    <Typography className="num">{selectedEmployee.insurance.employment.toLocaleString()}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>산재보험</Typography>
                    <Typography className="num">{selectedEmployee.insurance.industrial.toLocaleString()}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>원천세</Typography>
                    <Typography className="num">{selectedEmployee.tax.withholding.toLocaleString()}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>지방세</Typography>
                    <Typography className="num">{selectedEmployee.tax.local.toLocaleString()}</Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      공제 합계
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }} className="num">{(selectedEmployee.insurance.total + selectedEmployee.tax.total).toLocaleString()}</Typography>
                  </Box>
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  p: 2,
                  background: 'var(--primary)',
                  borderRadius: 1,
                  color: 'white',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  실수령액
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }} className="num">{selectedEmployee.netSalary.toLocaleString()}</Typography>
              </Box>
              {selectedEmployee.weeklyDetails.length > 0 &&
                renderWeeklyDetails(selectedEmployee.weeklyDetails)}
            </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayslipDialogOpen(false)}>닫기</Button>
          <Button variant="outlined" onClick={handleDownloadPdf}>
            다운로드
          </Button>
        </DialogActions>
      </Dialog>

      {selectedEmployeeForHoliday && (
        <WeeklyHolidayAllowanceDialog
          open={holidayAllowanceDialogOpen}
          onClose={() => {
            setHolidayAllowanceDialogOpen(false);
            setSelectedEmployeeForHoliday(null);
          }}
          employee={selectedEmployeeForHoliday.employee}
          schedules={schedules}
          selectedMonth={selectedMonth}
        />
      )}
    </Box>
  );
};

export default PayrollTab;
